import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Globe, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEOTab = ({ token }) => {
  const [seo, setSeo] = useState({
    site_title: '', meta_description: '', meta_keywords: '', og_title: '', og_description: '', og_image: '',
    robots_txt: '', google_analytics_id: '', google_merchant_feed_url: '/api/feed/google-merchant.xml',
    structured_data_enabled: true, canonical_url: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generatingSeo, setGeneratingSeo] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/seo`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setSeo(r.data))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/seo`, seo, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('SEO settings saved!');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleGenerateCategorySeo = async () => {
    setGeneratingSeo(true);
    try {
      const res = await axios.post(`${API}/admin/ai-generate-category-seo`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 60000
      });
      toast.success(`AI generated SEO for ${res.data.updated} of ${res.data.total} categories!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate SEO');
    } finally {
      setGeneratingSeo(false);
    }
  };

  if (loading) return <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;

  const Field = ({ label, field, rows, hint }) => (
    <div>
      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">{label}</label>
      {rows ? (
        <textarea value={seo[field] || ''} onChange={e => setSeo({...seo, [field]: e.target.value})} rows={rows}
          className="w-full px-3 py-2 border border-gray-200 rounded text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900 font-mono" />
      ) : (
        <input value={seo[field] || ''} onChange={e => setSeo({...seo, [field]: e.target.value})}
          className="w-full px-3 py-2 border border-gray-200 rounded text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900" />
      )}
      {hint && <p className="text-[10px] text-gray-400 mt-0.5">{hint}</p>}
    </div>
  );

  return (
    <div className="space-y-4" data-testid="admin-seo">
      <h2 className="text-xl font-bold text-gray-900">SEO Settings</h2>
      <div className="bg-white border border-gray-200 rounded p-5 space-y-4">
        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Page Meta Tags</h3>
        <Field label="Site Title" field="site_title" hint="Appears in browser tab and search results" />
        <Field label="Meta Description" field="meta_description" rows={3} hint="150-160 characters recommended for search results" />
        <Field label="Meta Keywords" field="meta_keywords" hint="Comma-separated keywords" />
        <Field label="Canonical URL" field="canonical_url" hint="e.g. https://posterfire.com" />
      </div>
      <div className="bg-white border border-gray-200 rounded p-5 space-y-4">
        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Open Graph (Social Sharing)</h3>
        <Field label="OG Title" field="og_title" hint="Title shown when shared on social media" />
        <Field label="OG Description" field="og_description" hint="Description shown on social media" />
        <Field label="OG Image URL" field="og_image" hint="1200x630px recommended" />
      </div>
      <div className="bg-white border border-gray-200 rounded p-5 space-y-4">
        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Technical SEO</h3>
        <Field label="robots.txt" field="robots_txt" rows={4} hint="Controls search engine crawling" />
        <Field label="Google Analytics ID" field="google_analytics_id" hint="e.g. G-XXXXXXXXXX" />
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Google Merchant Feed</label>
          <div className="flex items-center gap-2">
            <input value={seo.google_merchant_feed_url || '/api/feed/google-merchant.xml'} readOnly
              className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded text-sm text-gray-600 font-mono" />
            <button onClick={() => {navigator.clipboard.writeText(window.location.origin + '/api/feed/google-merchant.xml'); toast.success('Copied!');}}
              className="px-3 py-2 bg-gray-100 border border-gray-200 rounded text-sm font-medium hover:bg-gray-200">
              Copy URL
            </button>
            <button onClick={() => { const a = document.createElement('a'); a.href = `${API}/feed/google-merchant.xml`; a.download = 'google-merchant.xml'; a.click(); }}
              className="px-3 py-2 bg-gray-900 text-white rounded text-sm font-medium hover:bg-gray-800" data-testid="download-gmc-xml">
              Download
            </button>
          </div>
          <p className="text-[10px] text-gray-400 mt-0.5">Submit this URL to Google Merchant Center as your product feed</p>
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Sitemap</label>
          <div className="flex items-center gap-2">
            <input value="/api/sitemap.xml" readOnly className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded text-sm text-gray-600 font-mono" />
            <button onClick={() => {navigator.clipboard.writeText(window.location.origin + '/api/sitemap.xml'); toast.success('Copied!');}}
              className="px-3 py-2 bg-gray-100 border border-gray-200 rounded text-sm font-medium hover:bg-gray-200">
              Copy URL
            </button>
            <button onClick={() => { const a = document.createElement('a'); a.href = `${API}/sitemap.xml`; a.download = 'sitemap.xml'; a.click(); }}
              className="px-3 py-2 bg-gray-900 text-white rounded text-sm font-medium hover:bg-gray-800" data-testid="download-sitemap-xml">
              Download
            </button>
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Image Sitemap</label>
          <div className="flex items-center gap-2">
            <input value="/api/image-sitemap.xml" readOnly className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded text-sm text-gray-600 font-mono" />
            <button onClick={() => {navigator.clipboard.writeText(window.location.origin + '/api/image-sitemap.xml'); toast.success('Copied!');}}
              className="px-3 py-2 bg-gray-100 border border-gray-200 rounded text-sm font-medium hover:bg-gray-200">
              Copy URL
            </button>
            <button onClick={() => { const a = document.createElement('a'); a.href = `${API}/image-sitemap.xml`; a.download = 'image-sitemap.xml'; a.click(); }}
              className="px-3 py-2 bg-gray-900 text-white rounded text-sm font-medium hover:bg-gray-800" data-testid="download-image-sitemap-xml">
              Download
            </button>
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">robots.txt</label>
          <div className="flex items-center gap-2">
            <input value="/api/robots.txt" readOnly className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded text-sm text-gray-600 font-mono" />
            <button onClick={() => {navigator.clipboard.writeText(window.location.origin + '/api/robots.txt'); toast.success('Copied!');}}
              className="px-3 py-2 bg-gray-100 border border-gray-200 rounded text-sm font-medium hover:bg-gray-200">
              Copy URL
            </button>
            <button onClick={() => { fetch(`${API}/robots.txt`).then(r => r.text()).then(t => { const b = new Blob([t], {type:'text/plain'}); const a = document.createElement('a'); a.href = URL.createObjectURL(b); a.download = 'robots.txt'; a.click(); }); }}
              className="px-3 py-2 bg-gray-900 text-white rounded text-sm font-medium hover:bg-gray-800" data-testid="download-robots-txt">
              Download
            </button>
          </div>
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={seo.structured_data_enabled}
            onChange={e => setSeo({...seo, structured_data_enabled: e.target.checked})}
            className="w-4 h-4 rounded border-gray-300 accent-gray-900" />
          <span className="text-sm text-gray-700 font-medium">Enable JSON-LD Structured Data</span>
        </label>
      </div>
      {/* AI Category SEO */}
      <div className="bg-white border border-gray-200 rounded p-5 space-y-3">
        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Category SEO</h3>
        <p className="text-xs text-gray-500">Use AI to auto-generate optimized meta titles, descriptions, and keywords for all your product categories.</p>
        <button onClick={handleGenerateCategorySeo} disabled={generatingSeo}
          className="px-5 py-2.5 bg-gradient-to-r from-red-500 to-orange-500 text-white text-sm font-bold rounded hover:from-red-600 hover:to-orange-600 disabled:opacity-50 flex items-center gap-2 transition-all"
          data-testid="ai-generate-category-seo-btn">
          {generatingSeo ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating...</> : <><Sparkles className="w-4 h-4" /> Generate SEO for All Categories</>}
        </button>
      </div>
      <button onClick={handleSave} disabled={saving}
        className="px-6 py-2.5 bg-gray-900 text-white text-sm font-semibold rounded hover:bg-gray-700 disabled:opacity-40 flex items-center gap-2"
        data-testid="save-seo-btn">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Globe className="w-4 h-4" />}
        Save SEO Settings
      </button>
    </div>
  );
};


export default SEOTab;
